import React, { ReactElement, useEffect, useRef, useState } from "react";

interface IProps {
  visible: boolean;
  content: React.ReactNode;
  children: ReactElement;
  onVisibleChange?: (visible: boolean) => void;
  placement?: 'auto' | 'center'; // 新增placement属性
}

const SimplePopover: React.FC<IProps> = ({
  visible,
  content,
  children,
  onVisibleChange,
  placement = 'auto', // 默认为auto，保持向后兼容
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const visibleRef = useRef(visible);
  visibleRef.current = visible;
  const [myPlacement, setMyPlacement] = useState("");
  const [centerOffset, setCenterOffset] = useState(0);

  useEffect(() => {
    if (visible) {
      updatePlacement();
    } else {
      setMyPlacement("");
    }
  }, [visible]);

  const updatePlacement = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;
      const viewportWidth =
        window.innerWidth || document.documentElement.clientWidth;

      if (placement === 'center') {
        // 居中模式：气泡在上方或下方居中显示
        if (rect.top > viewportHeight / 2) {
          setMyPlacement("topCenter");
        } else {
          setMyPlacement("bottomCenter");
        }
        
        // 计算居中偏移量
        setTimeout(() => {
          if (popoverRef.current) {
            const popoverRect = popoverRef.current.getBoundingClientRect();
            const triggerCenter = rect.width / 2;
            const popoverCenter = popoverRect.width / 2;
            const offset = triggerCenter - popoverCenter;
            setCenterOffset(offset);
          }
        }, 0);
      } else {
        // 原有的auto模式
        if (rect.top > viewportHeight / 2) {
          if (rect.left > viewportWidth / 2) {
            setMyPlacement("topLeft");
          } else {
            setMyPlacement("topRight");
          }
        } else {
          if (rect.left > viewportWidth / 2) {
            setMyPlacement("bottomLeft");
          } else {
            setMyPlacement("bottomRight");
          }
        }
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onVisibleChange && onVisibleChange(false);
      }
    };
    if (visible) {
      document.addEventListener("click", handleClickOutside);
    }
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [visible, onVisibleChange]);

  const myClick = () => {
    onVisibleChange && onVisibleChange(!visible);
  };

  const getPopoverPositionClass = () => {
    if (placement === 'center') {
      const verticalClass = myPlacement.includes("bottom")
        ? "top-[calc(100%+6px)]"
        : "bottom-[calc(100%+6px)]";
      return `${verticalClass}`;
    } else {
      // 原有逻辑
      return `${
        myPlacement.includes("bottom")
          ? "top-[calc(100%+6px)]"
          : "bottom-[calc(100%+6px)]"
      } left-0`;
    }
  };

  const getPopoverStyle = () => {
    if (placement === 'center') {
      return { left: `${centerOffset}px` };
    }
    return {};
  };

  return (
    <div ref={ref} className="relative">
      <div onClick={myClick}>{children}</div>
      {visible && myPlacement && (
        <div
          ref={popoverRef}
          className={`absolute ${getPopoverPositionClass()} b-1 b-solid b-[rgba(0,0,0,0.1)] bg-white z-10 shadow-[0_8px_32px_0_rgba(25,29,38,0.16)] rounded-1.5`}
          style={getPopoverStyle()}
        >
          {content}
        </div>
      )}
    </div>
  );
};

export default SimplePopover;
